import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Quote } from "lucide-react";
import Disclaimer from "@/components/Disclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Share() {
    const { token } = useParams();
    const [insight, setInsight] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/share/${token}`)
            .then((r) => setInsight(r.data))
            .catch(() => setError("This insight can't be found or has been removed."));
    }, [token]);

    const pageUrl =
        typeof window !== "undefined" ? window.location.href : "";
    const ogImageUrl = `${API}/share/${token}/og.png`;
    const clean = (s, n = 200) => {
        if (!s) return "";
        const t = s.replace(/[*_`#]/g, "").trim();
        return t.length > n ? t.slice(0, n - 1) + "…" : t;
    };
    const metaTitle = insight
        ? clean(insight.headline, 80) + " · RealTalk AI"
        : "An insight from RealTalk AI";
    const metaDescription = insight
        ? clean(insight.suggestion || insight.body, 200)
        : "Anonymous life-advice AI. Real clarity, in minutes.";

    return (
        <div className="min-h-screen bg-rt-base text-rt-text flex flex-col">
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={pageUrl} />

                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="RealTalk AI" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={ogImageUrl} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={ogImageUrl} />
            </Helmet>

            <header className="sticky top-0 z-20 bg-rt-base/70 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-rt-muted hover:text-rt-text transition"
                        data-testid="share-home-link"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="font-serif text-base tracking-tight">
                            RealTalk
                        </span>
                    </Link>
                    <Link
                        to="/chat"
                        className="text-xs text-rt-muted hover:text-rt-text transition"
                        data-testid="share-chat-link"
                    >
                        Ask yours →
                    </Link>
                </div>
            </header>

            <main className="flex-1 px-4 sm:px-6 py-12 sm:py-20">
                <div className="max-w-2xl mx-auto">
                    <div className="text-xs uppercase tracking-[0.25em] text-rt-dim mb-5">
                        An insight from RealTalk&nbsp;AI
                    </div>

                    {error && (
                        <div
                            className="text-rt-muted text-sm"
                            data-testid="share-error"
                        >
                            {error}
                        </div>
                    )}

                    {insight && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="relative overflow-hidden rounded-3xl border border-rt-border bg-gradient-to-b from-rt-card to-rt-surface p-8 sm:p-12"
                            data-testid="share-card"
                        >
                            <Quote className="absolute top-6 right-6 w-10 h-10 text-rt-accent/20" />

                            <h1
                                className="font-serif text-2xl sm:text-3xl leading-snug tracking-tight text-rt-text"
                                data-testid="share-headline"
                            >
                                {insight.headline}
                            </h1>

                            {insight.body && (
                                <p
                                    className="mt-6 text-rt-muted text-base sm:text-[17px] leading-relaxed"
                                    data-testid="share-body"
                                >
                                    {insight.body}
                                </p>
                            )}

                            {insight.suggestion && (
                                <div className="mt-8 pt-6 border-t border-rt-border/70">
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-rt-accent mb-2">
                                        A grounded suggestion
                                    </div>
                                    <p
                                        className="font-serif italic text-lg sm:text-xl leading-snug text-rt-text"
                                        data-testid="share-suggestion"
                                    >
                                        {insight.suggestion}
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    )}

                    <div className="mt-10 text-center">
                        <Link
                            to={`/chat?ref=${encodeURIComponent(token)}`}
                            className="inline-flex items-center gap-2 rounded-full bg-[#B392F0] hover:bg-[#C8B1F5] text-black font-medium text-sm px-6 py-3 transition-all hover:-translate-y-[1px]"
                            data-testid="share-cta-btn"
                        >
                            Get clarity on your own
                        </Link>
                        <div className="mt-3 text-xs text-rt-dim">
                            Anonymous · No sign-up
                        </div>
                    </div>
                </div>
            </main>

            <Disclaimer />
        </div>
    );
}
